import React, { useState, useEffect, useRef } from 'react';
import classes from './Dashboard.module.css';
import HeaderNav from '../../Components/Dahboard Header/HeaderNav/HeaderNav';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  BarChart3,
  CalendarDays,
  ChevronDown,
  CircleDollarSign,
  ClipboardList,
  CreditCard,
  Landmark,
  RotateCcw,
  PieChart as PieChartIcon,
  UserRound,
  UsersRound,
  WalletCards,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  DefArea,
  LinearGradient
} from 'recharts';
import api from '../../Components/API/Api';
import { useUser } from '../../UserContext';
import adminAvatar from '../../Assets/assets/welcome/coopimg.jpg';

const Dashboard = () => {
  const [showBusinessInfoModal, setShowBusinessInfoModal] = useState(false);
  const [, setCheckingBusinessInfo] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();
  const dateInputRef = useRef(null);
const [selectedDate, setSelectedDate] = useState("");
  const [pendingApprovals, setPendingApprovals] = useState([]);
const [loadingPendingApprovals, setLoadingPendingApprovals] = useState(false);
const [dashboardMetrics, setDashboardMetrics] = useState(null);
const [metricsLoading, setMetricsLoading] = useState(false);
const [recentTransactions, setRecentTransactions] = useState([]);
const [recentTransactionsLoading, setRecentTransactionsLoading] = useState(false);
const userData = JSON.parse(localStorage.getItem('userData'));
// console.log(userData?.photograph, "userData");

  const resolveMetricValue = (source, keys) => {
    const roots = [source, source?.data, source?.data?.data, source?.result, source?.payload, source?.metrics];

    for (const root of roots) {
      if (!root || typeof root !== 'object') continue;

      for (const key of keys) {
        if (root[key] !== undefined && root[key] !== null) {
          return root[key];
        }

        const nestedValue = root?.[key]?.value;
        if (nestedValue !== undefined && nestedValue !== null) {
          return nestedValue;
        }
      }
    }

    return null;
  };

const formatMetricValue = (value, type = "number") => {
  const numericValue = Number(value);

  // Return 0.00 instead of "-"
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    Number.isNaN(numericValue)
  ) {
    return type === "currency" ? "₦0.00" : "0";
  }

  if (type === "percent") {
    return `${numericValue.toFixed(0)}%`;
  }

  if (type === "currency") {
    const formatShort = (num) => {
      if (num >= 1_000_000_000_000) {
        return `₦${(num / 1_000_000_000_000).toFixed(1).replace(".0", "")}T`;
      }

      if (num >= 1_000_000_000) {
        return `₦${(num / 1_000_000_000).toFixed(1).replace(".0", "")}B`;
      }

      if (num >= 1_000_000) {
        return `₦${(num / 1_000_000).toFixed(1).replace(".0", "")}M`;
      }

      if (num >= 1_000) {
        return `₦${(num / 1_000).toFixed(1).replace(".0", "")}K`;
      }

      return `₦${num.toLocaleString("en-NG", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    };

    return formatShort(numericValue);
  }

  if (Number.isInteger(numericValue)) {
    return new Intl.NumberFormat("en-NG").format(numericValue);
  }

  return numericValue.toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};
  
  const pageMap = {
    '/dashboard': 'Dashboard',
    '/transactions': 'Transactions',
    '/accounts': 'Accounting',
    '/reports': 'Reports',
    '/banking': 'Banking',
    '/settings': 'Settings',
  };

  // ... [Your existing business info check useEffect remains exactly the same] ...
  useEffect(() => {
    const checkBusinessInfoCompletion = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const hasPaidPlan = localStorage.getItem('hasPaidPlan') === 'true';
        if (!token) {
          setCheckingBusinessInfo(false);
          return;
        }
        if (hasPaidPlan) {
          setShowBusinessInfoModal(false);
          document.body.style.overflow = '';
          setCheckingBusinessInfo(false);
          return;
        }
        if (user && typeof user.onboarding !== 'undefined') {
          const onboardingStatus = String(user.onboarding);
          if (onboardingStatus === '0') {
            setShowBusinessInfoModal(true);
            document.body.style.overflow = 'hidden';
          }
          setCheckingBusinessInfo(false);
          return;
        }
        const companyIdHeader = (user && (user.company_id || user?.company?.id)) || localStorage.getItem('company_id') || null;
        const res = await api.get(`/v1/companies/get-info`, {
          headers: { company_id: companyIdHeader },
        });
        const item = Array.isArray(res?.data?.data) ? res.data.data[0] : (res?.data?.data || null);
        const isBusinessInfoIncomplete = !item || !item.name || item.name.trim() === '';
        if (isBusinessInfoIncomplete) {
          setShowBusinessInfoModal(true);
          document.body.style.overflow = 'hidden';
        }
      } catch (err) {
        console.error('Failed to fetch company info:', err);
        setShowBusinessInfoModal(true);
        document.body.style.overflow = 'hidden';
      } finally {
        setCheckingBusinessInfo(false);
      }
    };
    checkBusinessInfoCompletion();
  }, [user]);

  const handleRedirectToBusiness = () => {
    document.body.style.overflow = '';
    setShowBusinessInfoModal(false);
    navigate('/business');
  };

  const getCompanyId = () =>
    localStorage.getItem('company_id') ||
    localStorage.getItem('activeCompanyId') ||
    user?.company_id ||
    user?.company?.id ||
    '';

  const fetchPendingApprovals = async () => {
    setLoadingPendingApprovals(true);

    try {
      const res = await api.get('/v1/fetch_loan_applications');
      const applications = res.data?.data?.data || [];
      const pending = applications
        .filter((item) => Number(item?.approved) === 0)
        .slice(0, 5);
      setPendingApprovals(pending);
    } catch (error) {
      console.error(error);
      setPendingApprovals([]);
    } finally {
      setLoadingPendingApprovals(false);
    }
  };

  const fetchRecentTransactions = async () => {
    setRecentTransactionsLoading(true);

    try {
      const companyId = getCompanyId();
      const res = await api.get('/v1/customer/recent_transactions', {
        params: companyId ? { company_id: companyId } : {},
      });

      const payload = res?.data?.data ?? res?.data ?? null;
      const transactionItems = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.transactions)
        ? payload.transactions
        : [];

      setRecentTransactions(transactionItems);
    } catch (error) {
      console.error('Failed to fetch recent transactions:', error);
      setRecentTransactions([]);
    } finally {
      setRecentTransactionsLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingApprovals();
    fetchRecentTransactions();
  }, [user?.company_id, user?.company?.id]);

  const handleCloseModal = () => { /* Don't allow closing */ };

  useEffect(() => {
    const fetchDashboardMetrics = async () => {
      const companyId =
        localStorage.getItem('company_id') ||
        localStorage.getItem('activeCompanyId') ||
        user?.company_id ||
        user?.company?.id ||
        '';

      if (!companyId) {
        setDashboardMetrics(null);
        return;
      }

      setMetricsLoading(true);

      try {
        const params = {};
        if (companyId) params.company_id = companyId;
        if (selectedDate) params.date = selectedDate;

        const response = await api.get('/v1/customer/dashboard_metrics', { params });
        const payload = response?.data?.data ?? response?.data ?? null;
        setDashboardMetrics(payload);
        console.log('Fetched dashboard metrics:', payload);
      } catch (error) {
        console.error('Failed to fetch dashboard metrics:', error);
        setDashboardMetrics(null);
      } finally {
        setMetricsLoading(false);
      }
    };

    fetchDashboardMetrics();
  }, [selectedDate, user?.company_id, user?.company?.id]);

  console.log('Fetched dashboard metrics:', dashboardMetrics);

  const stats = [
    {
      label: 'Total Members',
      value: metricsLoading ? 'Loading...' : formatMetricValue(resolveMetricValue(dashboardMetrics, ['total_members', 'members', 'totalMembers', 'member_count', 'memberCount']), 'number'),
      change: '5.2%',
      tone: 'blue',
      icon: UsersRound,
    },
    {
      label: 'Total Savings Portfolio',
      value: metricsLoading ? 'Loading...' : formatMetricValue(resolveMetricValue(dashboardMetrics, ['total_savings_portfolio', 'savings_portfolio', 'total_savings', 'savings_balance', 'totalSavings']), 'currency'),
      change: '8.4%',
      tone: 'green',
      icon: WalletCards,
    },
    {
      label: 'Loan Portfolio Value',
      value: metricsLoading ? 'Loading...' : formatMetricValue(resolveMetricValue(dashboardMetrics, ['total_loans_applied', 'loan_portfolio', 'loan_balance', 'total_loans']), 'currency'),
      change: '6.1%',
      tone: 'purple',
      icon: PieChartIcon,
    },
    {
      label: 'Outstanding Loans',
      value: metricsLoading ? 'Loading...' : formatMetricValue(resolveMetricValue(dashboardMetrics, ['outstanding_loans', 'outstanding_loan_balance', 'loan_outstanding']), 'currency'),
      change: '4.3%',
      tone: 'orange',
      icon: CircleDollarSign,
    },
    {
      label: 'Total Collections',
      value: metricsLoading ? 'Loading...' : formatMetricValue(resolveMetricValue(dashboardMetrics, ['total_loan_repayments', 'collections', 'totalCollections']), 'currency'),
      change: '9.7%',
      tone: 'cyan',
      icon: Landmark,
    },
    {
      label: 'Recovery Rate',
      value: metricsLoading ? 'Loading...' : formatMetricValue(resolveMetricValue(dashboardMetrics, ['loan_recovery_rate', 'recoveryRate', 'recovery']), 'percent'),
      change: '2.4%',
      tone: 'mint',
      icon: RotateCcw,
      progress: Number(resolveMetricValue(dashboardMetrics, ['recovery_rate', 'recoveryRate', 'recovery'])) || 0,
    },
    {
      label: 'Total Defaulters',
      value: metricsLoading ? 'Loading...' : formatMetricValue(resolveMetricValue(dashboardMetrics, ['total_defaulters', 'defaulters', 'defaulting_members']), 'number'),
      change: '0%',
      tone: 'red',
      icon: UserRound,
      negative: true,
    },
    {
      label: 'Profit / Loss (YTD)',
      value: metricsLoading ? 'Loading...' : formatMetricValue(resolveMetricValue(dashboardMetrics, ['profit_loss', 'profit_loss', 'profitLoss']), 'currency'),
      change: '7.3%',
      tone: 'gold',
      icon: BarChart3,
    },
  ];

  const colors = ['#2dd4bf', '#3b82f6', '#facc15', '#ef4444', '#8b5cf6'];
  
  const loanDistribution = Array.isArray(dashboardMetrics?.metrics?.major_5_loans)
    ? dashboardMetrics?.metrics?.major_5_loans.map((loan, index) => ({
        name: loan.loan_type || `Loan ${index + 1}`,
        value: loan.percentage ? Math.round(loan.percentage) : 0,
        color: colors[index % colors.length],
      }))
    : [
        { name: 'Personal Loans', value: 45, color: '#2dd4bf' },
        { name: 'Asset Loans', value: 25, color: '#3b82f6' },
        { name: 'Emergency Loans', value: 15, color: '#facc15' },
        { name: 'Housing Loans', value: 10, color: '#ef4444' },
        { name: 'Education Loans', value: 5, color: '#8b5cf6' },
      ];

  const monthToShort = (month) => {
    const shortMonths = {
      January: 'JAN', February: 'FEB', March: 'MAR', April: 'APR',
      May: 'MAY', June: 'JUN', July: 'JUL', August: 'AUG',
      September: 'SEP', October: 'OCT', November: 'NOV', December: 'DEC',
    };
    return shortMonths[month] || month.substring(0, 3).toUpperCase();
  };

  const trendData = Array.isArray(dashboardMetrics?.metrics?.savings_by_month)
    ? dashboardMetrics?.metrics?.savings_by_month.map((item) => ({
        month: monthToShort(item.month),
        value: Math.floor(Number(item.amount) / 1_000_000) || 0,
      }))
    : [
        { month: 'JAN', value: 0 }, { month: 'FEB', value: 0 },
        { month: 'MAR', value: 0 }, { month: 'APR', value: 0 },
        { month: 'MAY', value: 0 }, { month: 'JUN', value: 0 },
        { month: 'JUL', value: 0 }, { month: 'AUG', value: 0 },
        { month: 'SEP', value: 0 }, { month: 'OCT', value: 0 },
        { month: 'NOV', value: 0 }, { month: 'DEC', value: 0 },
      ];

  
      



  const parseChartValue = (amount) => {
    const parsed = Number(amount);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const formatChartTick = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount === 0) return '0';
    const abs = Math.abs(amount);
    if (abs >= 1_000_000_000) {
      return `₦${(amount / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
    }
    if (abs >= 1_000_000) {
      return `₦${(amount / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
    }
    if (abs >= 1_000) {
      return `₦${(amount / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
    }
    return `₦${amount.toLocaleString('en-NG')}`;
  };

  const loanRepaymentData = Array.isArray(dashboardMetrics?.metrics?.loan_repayments_by_month)
    ? dashboardMetrics?.metrics?.loan_repayments_by_month.map((item) => ({
        month: monthToShort(item.month),
        value: parseChartValue(item.amount),
      }))
    : [
        { month: 'JAN', value: 0 }, { month: 'FEB', value: 0 },
        { month: 'MAR', value: 0 }, { month: 'APR', value: 0 },
        { month: 'MAY', value: 0 }, { month: 'JUN', value: 0 },
        { month: 'JUL', value: 0 }, { month: 'AUG', value: 0 },
        { month: 'SEP', value: 0 }, { month: 'OCT', value: 0 },
        { month: 'NOV', value: 0 }, { month: 'DEC', value: 0 },
      ];;

  const savingsWithdrawalData = Array.isArray(dashboardMetrics?.metrics?.savings_withdrawals_by_month)
    ? dashboardMetrics?.metrics?.savings_withdrawals_by_month.map((item) => ({
        month: monthToShort(item.month),
        value: parseChartValue(item.amount),
      }))
    : [
        { month: 'JAN', value: 0 }, { month: 'FEB', value: 0 },
        { month: 'MAR', value: 0 }, { month: 'APR', value: 0 },
        { month: 'MAY', value: 0 }, { month: 'JUN', value: 0 },
        { month: 'JUL', value: 0 }, { month: 'AUG', value: 0 },
        { month: 'SEP', value: 0 }, { month: 'OCT', value: 0 },
        { month: 'NOV', value: 0 }, { month: 'DEC', value: 0 },
      ];;

  const formatTransactionAmount = (value) => {
    const amount = Number(value);
    if (Number.isNaN(amount)) return String(value || '₦0.00');
    return amount.toLocaleString('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const normalizeTransaction = (item, index) => ({
    id: item.id ?? item.transaction_id ?? `${item.type || item.transaction_type || 'txn'}-${index}`,
    type: item.transaction_type || item.type || item.activity || item.description || 'Transaction',
    name: item.customer_name || item.member_name || item.payer_name || item.payee_name || item.name || 'Unknown',
    time: item.time || item.transaction_time || item.created_at || item.date || '',
    amount: formatTransactionAmount(item.amount ?? item.value ?? item.payment_amount ?? item.total ?? item.transaction_amount),
    trend: item.trend || (String(item.transaction_type || item.type || '').toLowerCase().includes('withdrawal') ? 'down' : 'up'),
  });

  const transactions =
    recentTransactions.length > 0
      ? recentTransactions.map(normalizeTransaction)
      : [];

  const toSentenceCase = (text) => {
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

  return (
    <div className={classes.body}>
      <HeaderNav activePage={pageMap[location.pathname]} />
      <main className={classes.dashboardShell}>
        
        {/* Header / Hero Row */}
        <section className={classes.heroRow}>
          <div className={classes.welcomeCluster}>
           {userData?.photograph ? (
  <img
    className={classes.avatar}
    src={userData.photograph}
    alt={`${userData?.first_name || ""} ${userData?.second_name || ""}`}
    onError={(e) => {
      e.target.src = adminAvatar; // fallback if image fails to load
    }}
  />
) : (
  <div className={classes.avatarFallback}>
    {(userData?.first_name?.charAt(0) || "").toUpperCase()}
    {(userData?.last_name?.charAt(0) || "").toUpperCase()}
  </div>
)}
            <div>
              <h1>Welcome {userData?.name || "Admin User"}</h1>
              <p>Business Performance Overview for {toSentenceCase(userData?.company?.name)}. </p>
            </div>
          </div>
         <div className={classes.filterCluster}>
  <button
    type="button"
    className={classes.dateButton}
    onClick={() =>
      dateInputRef.current?.showPicker?.() ||
      dateInputRef.current?.click()
    }
  >
    <CalendarDays size={18} />
    <span>{selectedDate || "This Year"}</span>
    <ChevronDown size={17} />
  </button>

  {selectedDate && (
    <button
      className={classes.clearFilter}
      onClick={() => setSelectedDate("")}
      style={{color: 'red', cursor: 'pointer'}}
    >
      Clear Filter
    </button>
  )}

  <input
    ref={dateInputRef}
    type="date"
    value={selectedDate}
    onChange={(e) => setSelectedDate(e.target.value)}
    style={{
      position: "absolute",
      opacity: 0,
      pointerEvents: "none",
      width: 0,
      height: 0,
    }}
  />
</div>
        </section>

        {/* Stat Cards */}
        <section className={classes.statsGrid} aria-label="Cooperative overview metrics">
          {stats.map((item) => {
            const Icon = item.icon;
            return (
              <article key={item.label} className={`${classes.statCard} ${classes[item.tone]}`}>
                <div className={classes.statLeft}>
                  <p className={classes.statLabel}>{item.label}</p>
                  <strong className={classes.statValue}>{item.value}</strong>
                  <span className={item.negative ? classes.negativeChange : classes.positiveChange}>
                    {item.negative ? <ArrowDown size={13} /> : <ArrowUp size={13} />}
                    {item.change} vs last month
                  </span>
                </div>
                <div className={classes.statRight}>
                  {item.progress ? (
                    <div className={classes.progressRing} style={{ '--progress': `${item.progress}%` }}>
                      <span>{item.progress}%</span>
                    </div>
                  ) : (
                    <div className={classes.statIcon}>
                      {Icon && <Icon size={26} strokeWidth={2.4} aria-hidden="true" />}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </section>

        {/* Chart Row 1: Loan Distribution + Savings Trend */}
        <section className={classes.chartGrid}>
          <article className={classes.panel}>
            <div className={classes.panelHeader}>
              <h2>Major Loan Portfolio</h2>
            </div>
            <div className={classes.piePanelBody}>
              <div className={classes.pieChartWrap}>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={loanDistribution}
                      dataKey="value"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={0}
                      stroke="none"
                    >
                      {loanDistribution.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `${value}%`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className={classes.legendList}>
                {loanDistribution.map((item) => (
                  <div className={classes.legendItem} key={item.name}>
                    <span className={classes.legendName}>
                      <i style={{ backgroundColor: item.color }} />
                      {item.name}
                    </span>
                    <strong>{item.value}%</strong>
                  </div>
                ))}
              </div>
            </div>
          </article>

          <article className={classes.panel}>
            <div className={classes.panelHeader}>
              <h2>Monthly Savings Trend</h2>
              {/* <button type="button" className={classes.yearButton}>
                This Year <ChevronDown size={13} />
              </button> */}
            </div>
            <div className={classes.chartWrap}>
              <ResponsiveContainer width="100%" height={270}>
                <AreaChart data={trendData} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="savingsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke="#f1f5f9" strokeDasharray="4 4" />
                  <XAxis
                    dataKey="month"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 10 }}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 10 }}
                    ticks={[0, 100, 200, 300, 400]}
                    tickFormatter={(value) => (value === 0 ? '0' : `${value}M`)}
                  />
                  <Tooltip formatter={(value) => [`₦${value}M`, 'Savings']} />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fill="url(#savingsGradient)"
                    dot={{ r: 4, fill: '#ffffff', stroke: '#3b82f6', strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </article>
        </section>

        {/* Chart Row 2: Loan Recovery + Monthly Collections */}
        <section className={classes.chartGrid}>
          <article className={classes.panel}>
            <div className={classes.panelHeader}>
              <h2>Savings Withdrawal Trend</h2>
              {/* <button type="button" className={classes.yearButton}>
                This Year <ChevronDown size={13} />
              </button> */}
            </div>
            <div className={classes.chartWrap}>
              <ResponsiveContainer width="100%" height={270}>
                <AreaChart data={savingsWithdrawalData} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="withdrawalGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke="#f1f5f9" strokeDasharray="4 4" />
                  <XAxis
                    dataKey="month"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 10 }}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 10 }}
                    tickFormatter={formatChartTick}
                  />
                  <Tooltip formatter={(value) => [formatChartTick(value), 'Withdrawal']} />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#22c55e"
                    strokeWidth={2}
                    fill="url(#withdrawalGradient)"
                    dot={{ r: 4, fill: '#ffffff', stroke: '#22c55e', strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </article>

          <article className={classes.panel}>
            <div className={classes.panelHeader}>
              <h2>Loan Repayment Trend</h2>
              {/* <button type="button" className={classes.yearButton}>
                This Year <ChevronDown size={13} />
              </button> */}
            </div>
            <div className={classes.chartWrap}>
              <ResponsiveContainer width="100%" height={270}>
                <BarChart data={loanRepaymentData} barCategoryGap={18}>
                  <CartesianGrid vertical={false} stroke="#f1f5f9" strokeDasharray="4 4" />
                  <XAxis
                    dataKey="month"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 10 }}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 10 }}
                    ticks={[0, 100, 200, 300, 400]}
                    tickFormatter={(value) => (value === 0 ? '0' : `${value}M`)}
                  />
                  <Tooltip formatter={(value) => [`₦${value}M`, 'Collections']} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]} fill="#3b82f6" barSize={14} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </article>
        </section>

        {/* Table Row */}
        <section className={classes.tableGrid}>
          <article className={classes.tablePanel}>
            <div className={classes.panelHeader}>
              <h2>Pending Approvals</h2>
              <button type="button" className={classes.viewAll} onClick={() => navigate('/loans/loan-approval')}>
             {loadingPendingApprovals ? '' :   'View All'}
              </button>
            </div>
            <div className={classes.listRows}>
              {loadingPendingApprovals ? (
  <div style={{ padding: "20px", textAlign: "center" }}>
    Loading...
  </div>
) : pendingApprovals.length === 0 ? (
  <div style={{ padding: "20px", textAlign: "center" }}>
    No pending approvals.
  </div>
) : (
  pendingApprovals.map((item) => (
                <div
  className={classes.approvalRow}
  key={item.id}
>
  <span className={`${classes.listIcon} ${classes.blue}`}>
    <CreditCard color='#3b82f6' size={15} />
  </span>

  <p>
    {item.member?.name}
  </p>

  <span className={classes.approvalCount}>
    {Number(item.amount || item.principal_amount || 0).toLocaleString("en-NG", {
      style: "currency",
      currency: "NGN",
    })}
  </span>

  {/* <button
    type="button"
    className={classes.reviewBtn}
  >
    Review
  </button> */}
</div>
  )))}
            </div>
          </article>

          <article className={classes.tablePanel}>
            <div className={classes.panelHeader}>
              <h2>Recent Transactions</h2>
              {/* <button type="button" className={classes.viewAll}>View All</button> */}
            </div>
            <div className={classes.listRows}>
              {recentTransactionsLoading ? (
                <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>
              ) : recentTransactions.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center' }}>No recent transactions.</div>
              ) : (
                transactions.map((item) => (
                  <div className={classes.transactionRow} key={item.id}>
                    <span className={`${classes.listIcon} ${classes[item.trend]}`}>
                      <CreditCard size={15} />
                    </span>
                    <p className={classes.transactionDesc}>{item.type} - {item.name}</p>
                    <p className={classes.transactionTime}>{item.transaction_date}</p>
                    <strong className={classes[`${item.trend}Amount`]}>{item.amount}</strong>
                  </div>
                ))
              )}
            </div>
          </article>
        </section>

      </main>

      {/* Business Info Modal (unchanged) */}
      {showBusinessInfoModal && (
        <div className={classes.modalOverlay} onClick={handleCloseModal}>
          <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={classes.modalHeader}>
              <h2 className={classes.modalTitle}>Complete Your Business Information</h2>
            </div>
            <div className={classes.modalBody}>
              <p className={classes.modalMessage}>
                It looks like you haven't completed your business information yet. 
                Please complete your business details to continue using the platform.
              </p>
            </div>
            <div className={classes.modalFooter}>
              <button className={classes.modalButton} onClick={handleRedirectToBusiness}>
                Complete Business Information
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;